import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";

import { Form } from "semantic-ui-react";
import { createValidator } from "not-valid";

import { EditComponent, EditProps as SharedEditProps, SaveDispatchProps } from "@neworbit/simpleui-forms";
import { Input } from "@neworbit/simpleui-input";

import { MedicalTypeId } from "@teamprevent/common/healthSurveillance/models";
import { isYesNoOptionEnabledOptions } from "@teamprevent/common/model";
import { WorkGroupDetailModel } from "@teamprevent/common/workGroup/model";
import { toast } from "@teamprevent/common/toasts";

import { CustomerListModel, CustomerState, selectors } from "../../customer";
import { loadWorkGroups, saveWorkGroup } from "../actions";
import { AppState, WorkGroupEditModel, containsOnlyPitPupMedicals, areRespiratoryLevel1MedicalsValid, areHavsMedicalsValid } from "../model";
import { basePathSelector, workGroupSelector, SelectorState, workGroupsSelector } from "../selectors";

import { EditModal } from "./EditModal";
import { BaseProps } from "./Base";

import { push } from "redux-little-router";

export interface EditProps extends SharedEditProps<WorkGroupEditModel> {
    open: boolean;
    customer: CustomerListModel;
    workGroups: WorkGroupDetailModel[];
    onMedicalsChanged: (newMedicals: MedicalTypeId[]) => void;
}

export interface DispatchProps extends SaveDispatchProps<WorkGroupEditModel> {
    loadWorkGroups: (customerId: string) => void;
    close: () => void;
}

export class EditUnconnected extends EditComponent<WorkGroupEditModel, EditProps & DispatchProps & BaseProps> {
    public componentDidMount() {
        if (this.props.customer?.id) {
            this.props.loadWorkGroups(this.props.customer.id);
        }
    }

    public componentDidUpdate(prevProps: EditProps) {
        if (!prevProps.customer?.id && this.props.customer?.id) {
            this.props.loadWorkGroups(this.props.customer.id);
        }
    }

    private notDuplicate = createValidator<string>(
        (value) =>
            value === this.props.model.name ||
            !this.props.workGroups ||
            !Array.isArray(this.props.workGroups) ||
            !this.props.workGroups.some((wg) => wg.name === value),
        "Work Group with this name already exists"
    );

    public render() {
        return (
            <Form onSubmit={this.handleSubmit}>
                <Input.Text
                    value={this.state.values.name}
                    label="Name"
                    required={true}
                    showErrors={this.state.showErrors}
                    onChange={this.onUpdateName}
                    validation={[this.notDuplicate]}
                />
                <Input.DropdownMulti
                    value={this.state.values.medicals}
                    options={this.props.medicalTypeOptions}
                    label="Medicals"
                    required={true}
                    showErrors={this.state.showErrors}
                    onChange={this.onUpdateMedicals}
                    search
                />
                <Input.Dropdown
                    value={containsOnlyPitPupMedicals(this.state.values.medicals) ? this.state.values.isFastTrackPitPupSpecific?.toString() : "false" }
                    label="Fast Track"
                    options={isYesNoOptionEnabledOptions.map(c => ({ text: c.text, value: c.value }))}
                    showErrors={this.state.showErrors}
                    onChange={(value, valid) => this.updateProperty("isFastTrackPitPupSpecific", value === "true", valid)}
                    disabled={containsOnlyPitPupMedicals(this.state.values.medicals) === false}
                />
                <Form.Field>
                    <label>Customer</label>
                    <span>{this.props.customer.name}</span>
                </Form.Field>
            </Form>
        );
    }

    public submit(): void {
        if (!areRespiratoryLevel1MedicalsValid(this.state.values.medicals)) {
            // eslint-disable-next-line max-len
            toast.warning("I am sorry, you are unable to save this medical group unless you have neither or both 'Respiratory Sensitiser Face to Face - Level 1' and 'Respiratory Sensitiser Questionnaire - Level 1'");
            return;
        }
        if (!areHavsMedicalsValid(this.state.values.medicals)) {
            // eslint-disable-next-line max-len
            toast.warning("I am sorry, you are Unable to save this medical group unless you have both 'HAVS Questionnaire' and 'HAVS Face to Face' selected, or if neither is selected");
            return;
        }

        this.handleSubmit({ preventDefault: (): void => undefined } as any);
    }

    private onUpdateName = (value: string, valid: boolean) => this.updateProperty("name", value, valid);

    private onUpdateMedicals = (value: MedicalTypeId[], valid: boolean) => {
        const isFastTrackPitPupSpecific = containsOnlyPitPupMedicals(value);
        this.updateProperty("isFastTrackPitPupSpecific", isFastTrackPitPupSpecific, valid);

        this.setState({
            values: { ...this.state.values, medicals: value, isFastTrackPitPupSpecific },
            valid: { ...this.state.valid, medicals: valid }
        }, () => this.props.onMedicalsChanged(value));
    };
}

function mapStateToProps(state: AppState & CustomerState & SelectorState) {
    return {
        customer: selectors.customerSelector(state),
        workGroups: workGroupsSelector(state),
        model: workGroupSelector(state) as WorkGroupEditModel,
        open: state.router.pathname!.endsWith("/edit"),
        basePath: basePathSelector(state)
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<AppState, undefined, any>) {
    return {
        dispatchLoadWorkGroups: (customerId: string) => dispatch(loadWorkGroups({ customerId })),
        dispatchSave: (customerId: string, workGroup: WorkGroupEditModel, basePath: string) =>
            dispatch(saveWorkGroup(customerId, workGroup, basePath)),
        dispatchClose: (basePath: string) => dispatch(push(basePath, {}))
    };
}

function mergeProps(propsFromState: any, propsFromDispatch: any, baseProps: BaseProps): EditProps & DispatchProps & BaseProps {
    return {
        ...baseProps,
        customer: propsFromState.customer,
        workGroups: propsFromState.workGroups,
        model: propsFromState.model,
        open: propsFromState.open,
        onMedicalsChanged: propsFromState.onMedicalsChanged,
        save: (workGroup: WorkGroupEditModel) => propsFromDispatch.dispatchSave(propsFromState.customer.id, workGroup, propsFromState.basePath),
        close: () => propsFromDispatch.dispatchClose(`${propsFromState.basePath}/${propsFromState.model.id}`),
        loadWorkGroups: (customerId: string) => propsFromDispatch.dispatchLoadWorkGroups(customerId),
    };
}

export const Edit = connect(mapStateToProps, mapDispatchToProps, mergeProps)(EditModal);
